// API Service para comunicação com o backend
// Adapter layer: converte contratos do backend para interfaces internas do frontend

const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

// Prefixo de rota do backend
const API_PREFIX = '/api/tcmformfactory/v1';

// ============================================
// Tipos internos do frontend (consumers usam estes)
// ============================================

export interface CustomerInfo {
  name: string;
  email?: string;
  cpf_cnpj: string;
  type: 'PF' | 'PJ';
}

export interface RequiredDocumentInfo {
  id: string;
  name: string;
  description?: string;
  required: boolean;
}

export interface FormConfigInfo {
  required_documents: RequiredDocumentInfo[];
}

export interface ValidateTokenResponse {
  valid: boolean;
  error?: 'TOKEN_EXPIRED' | 'TOKEN_CONSUMED' | 'TOKEN_INVALID';
  customer?: CustomerInfo;
  form_config?: FormConfigInfo;
}

export interface UploadedFileInfo {
  field_name: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  storage_key?: string;
}

export interface SubmitFormRequest {
  token: string;
  personal_data?: Record<string, unknown>;
  address_data?: unknown;
  credit_questionnaire?: Record<string, unknown>;
  authorizations?: unknown;
  uploaded_files?: {
    field_name: string;
    file_name: string;
    file_size: number;
    mime_type: string;
    storage_key: string;
    tags?: Record<string, string>;
  }[];
}

export interface SubmitFormResponse {
  success: boolean;
  message: string;
  submission_id?: string;
  error?: string;
}

// ============================================
// Tipos da response real do backend
// ============================================

/** Response real do backend para validate-token (wrapper com valid, customer, form_config) */
interface BackendValidateTokenResponse {
  valid: boolean;
  error?: string;
  customer?: {
    name: string;
    email?: string;
    cpf_cnpj: string;
    type: 'PF' | 'PJ';
  };
  form_config?: {
    required_documents: Array<{
      id: string;
      name: string;
      description?: string;
      required: boolean;
    }>;
  };
}

/** Response real do backend para upload-url */
interface BackendPresignedResponse {
  upload_url: string;
  file_key: string;
  expires_at: string;
  headers: Record<string, string>;
  max_file_size: number;
}

// ============================================
// Classe de erro
// ============================================

export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errorCode?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ============================================
// Fetch helpers
// ============================================

/** Fetch genérico com JSON parse e error handling */
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    // Response body não é JSON (ex.: página 404 HTML)
    if (!response.ok) {
      throw new ApiError(
        `Erro ${response.status}: ${response.statusText}`,
        response.status
      );
    }
    throw new Error('Resposta inválida do servidor (não é JSON)');
  }

  if (!response.ok) {
    const errBody = data as Record<string, string>;
    throw new ApiError(
      errBody?.message || 'Erro na requisição',
      response.status,
      errBody?.error
    );
  }

  return data as T;
}

// ============================================
// API Functions — com adapter para contrato do backend
// ============================================

/**
 * Valida o token de acesso e retorna os dados do cliente.
 *
 * Backend retorna { valid, customer, form_config } em HTTP 200,
 * ou { valid: false, error } em HTTP 401.
 */
export async function validateToken(token: string): Promise<ValidateTokenResponse> {
  try {
    const raw = await fetchApi<BackendValidateTokenResponse>(
      `${API_PREFIX}/validate-token?token=${encodeURIComponent(token)}`,
      { method: 'GET' }
    );

    // Backend já retorna no formato esperado pelo frontend
    return {
      valid: raw.valid,
      customer: raw.customer,
      form_config: raw.form_config
        ? { required_documents: raw.form_config.required_documents }
        : undefined,
    };
  } catch (err) {
    // Backend retorna HTTP 401 com { valid: false, error: "TOKEN_xxx" } no body
    if (err instanceof ApiError) {
      return {
        valid: false,
        error: (err.errorCode as ValidateTokenResponse['error']) || 'TOKEN_INVALID',
      };
    }
    throw err;
  }
}

/**
 * Submete o formulário preenchido.
 *
 * Backend retorna HTTP 201 com { submission_id, protocol_number, status, message }.
 * Em caso de erro retorna HTTP 4xx/5xx com { error, message }.
 */
export async function submitForm(data: SubmitFormRequest): Promise<SubmitFormResponse> {
  try {
    const raw = await fetchApi<{ submission_id: string; protocol_number: string; status: string; message: string }>(
      `${API_PREFIX}/submit-form`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${data.token}`,
        },
        body: JSON.stringify(data),
      }
    );

    // HTTP 201 — sucesso
    return {
      success: true,
      message: raw.message,
      submission_id: raw.submission_id,
    };
  } catch (err) {
    if (err instanceof ApiError) {
      return {
        success: false,
        message: err.message,
        error: err.errorCode,
      };
    }
    throw err;
  }
}

/**
 * Health check da API
 */
export async function healthCheck(): Promise<{ status: string; service: string }> {
  return fetchApi<{ status: string; service: string }>('/health', {
    method: 'GET',
  });
}

// ============================================
// Upload de Arquivos
// ============================================

export interface UploadConfigResponse {
  allowed_extensions: string[];
  max_file_size_mb: number;
  max_file_size_bytes: number;
}

export interface GenerateUploadURLRequest {
  token: string;
  file_name: string;
  file_size: number;
  document_type: string;
}

export interface PresignedUploadURL {
  upload_url: string;
  file_key: string;
  expires_at: string;
  headers: Record<string, string>;
  max_file_size: number;
}

/**
 * Obtém configuração de upload (extensões permitidas, tamanho máximo)
 */
export async function getUploadConfig(): Promise<UploadConfigResponse> {
  return fetchApi<UploadConfigResponse>(`${API_PREFIX}/upload-config`, {
    method: 'GET',
  });
}

/**
 * Gera URL pré-assinada para upload de arquivo.
 *
 * D3: Backend retorna s3_key (não file_key) e expires_in em segundos (não expires_at datetime).
 * Adapter normaliza para o formato interno do frontend.
 */
export async function generateUploadURL(data: GenerateUploadURLRequest): Promise<PresignedUploadURL> {
  const raw = await fetchApi<BackendPresignedResponse>(
    `${API_PREFIX}/upload-url`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${data.token}`,
      },
      body: JSON.stringify(data),
    }
  );

  return {
    upload_url: raw.upload_url,
    file_key: raw.file_key,
    expires_at: raw.expires_at,
    headers: raw.headers,
    max_file_size: raw.max_file_size,
  };
}

/**
 * Faz upload do arquivo para o S3 via proxy no backend (evita CORS do S3)
 */
export async function uploadFile(
  token: string,
  file: File,
  documentType: string,
  onProgress?: (progress: number) => void
): Promise<UploadedFileInfo> {
  const allowedExtensions = [
    '.pdf',
    '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp',
    '.xls', '.xlsx', '.xlsm', '.csv',
  ];
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

  if (!allowedExtensions.includes(fileExtension)) {
    throw new Error(`Extensão não permitida: ${fileExtension}. Tipos aceitos: PDF, imagens (JPG, PNG, GIF) e planilhas (XLS, XLSX, CSV).`);
  }

  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error(`Arquivo muito grande: ${(file.size / 1024 / 1024).toFixed(2)}MB. Tamanho máximo: 10MB`);
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);

  const url = `${API_BASE_URL}${API_PREFIX}/upload`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText);
          resolve({
            field_name: documentType,
            file_name: result.file_name,
            file_size: result.file_size,
            mime_type: result.mime_type,
            storage_key: result.file_key,
          });
        } catch {
          reject(new Error('Resposta inválida do servidor'));
        }
      } else {
        reject(new Error(`Upload falhou: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Erro de rede durante o upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelado'));
    });

    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
}

export default {
  validateToken,
  submitForm,
  healthCheck,
  getUploadConfig,
  generateUploadURL,
  uploadFile,
};
